<!-- ResultsChart.svelte -->

<script>
    import {
        Chart,
        BarController,
        CategoryScale,
        LinearScale,
        BarElement,
        Tooltip,
    } from "chart.js";
    import ChartDataLabels from "chartjs-plugin-datalabels";

    // register only what we use → smaller bundle
    Chart.register(
        BarController,
        CategoryScale,
        LinearScale,
        BarElement,
        Tooltip,
        ChartDataLabels,
    );

    /* ---------- INPUT PROPS ---------- */
    let {
        data = [0.32, 0.1, 0.36],
        labels = [
            "Reject the offer",
            "Take the offer and move",
            "Try to negotiate remote working conditions so I don't have to move",
        ],
        colors = [
            // Importante, si se cambia el alpha (último número), hay que cambiar también la regla de los backgroundColors (más abajo)
            "rgba(255,99,132,0.9)",
            "rgba(54,162,235,0.9)",
            "rgba(255,206,86,0.9)",
            "rgba(75,192,192,0.9)",
            "rgba(153,102,255,0.9)",
            "rgba(255,159,64,0.9)",
        ],
        showStars = true, // Controls whether to show star emojis on the top result
        textPositionThreshold = 0.15, // Threshold below which text is positioned to the right of bars
        showGridLines = true, // Controls whether to show background grid lines

        // Paleta alternativa con más contraste (Habría que cambiar tb en la otra gráfica)
        /*     colors = [
        "rgba(27,158,119,0.9)",  // teal-green
        "rgba(217,95,  2,0.9)",  // vivid orange
        "rgba(117,112,179,0.9)", // violet-blue
        "rgba(231,41,138,0.9)",  // magenta-pink
        "rgba(102,166,30,0.9)",  // leaf-green
        "rgba(230,171, 2,0.9)",  // golden-yellow
        ] */
    } = $props();

    let canvas;
    let chart;

    // Determine label truncation length once (mobile ≤600 px → 15, otherwise 25).
    // Falls back to 25 during SSR or if `window` is unavailable.
    const maxLabelLength =
        typeof window !== "undefined" && window.matchMedia("(max-width: 600px)").matches
            ? 15
            : 25;

    /* ---------- BUILD CHART ---------- */
    function buildChart() {
        Chart.defaults.font.family = "'IBM Plex Mono', monospace";
        
        // ✂️  Clean up any previous instance
        if (chart) chart.destroy();

        /* zip → sort → unzip so bars render biggest → smallest */
        const rows = data
            .map((v, i) => ({ v, label: labels[i], color: colors[i] }))
            .sort((a, b) => b.v - a.v);

        const values = rows.map((r) => r.v);
        const sortedLabels = rows.map((r) => r.label);
        const sortedColors = rows.map((r) => r.color);
        const maxValue = Math.max(...values);

        chart = new Chart(canvas, {
            type: "bar",
            data: {
                labels: sortedLabels,
                datasets: [
                    {
                        data: values,
                        borderWidth: 2.5,
                        backgroundColor: sortedColors.map((c) =>
                            c.replace("0.9", "0.15"),
                        ),
                        borderColor: sortedColors,
                        barPercentage: 1,
                        categoryPercentage: 1,
                        borderRadius: 8,
                    },
                ],
            },
            options: {
                indexAxis: "y",
                responsive: true,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        enabled: true,
                        displayColors: false,

                        /* prettier number on top (title) */
                        titleFont: { size: 20, weight: "bold" }, // number stays bold
                        callbacks: {
                            /* number becomes the title (= first line) */
                            title: (ctx) => ` ${Math.round(ctx[0].parsed.x * 100)}`, // ctx is array in title

                            /* label becomes second line, wrapped every ~40 chars */
                            label: (ctx) =>
                                ctx.label.match(/.{1,40}/g) || ctx.label, // return array → multi-line
                        },

                        /* body text non-bold & a bit smaller */
                        bodyFont: { size: 14, weight: "normal" }, // plain weight
                    },
                    datalabels: {

                        anchor: (ctx) =>
                            ctx.dataset.data[ctx.dataIndex] < textPositionThreshold
                                ? "end"
                                : "center",
                        align: (ctx) =>
                            ctx.dataset.data[ctx.dataIndex] < textPositionThreshold
                                ? "right"
                                : "center",
                        // color: (ctx) => ctx.dataset.borderColor[ctx.dataIndex],
                        // color: '#4a4a4a',          // ← forces pure-black text
                        formatter(value, ctx) {
                            let label = ctx.chart.data.labels[ctx.dataIndex];
                            const maxL = maxLabelLength;
                            if (label.length > maxL)
                                label = label.slice(0, maxL) + "…";
                            return value === maxValue && showStars
                                ? `⭐ ${label} ⭐`
                                : label;
                        },
                        font: (ctx) => {
                            const isMax =
                                ctx.dataset.data[ctx.dataIndex] === maxValue;
                            return {
                                size: isMax ? 25 : 14,
                                weight: isMax ? "bold" : "normal",
                            };
                        },
                        display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 0,
                    },
                },
                scales: {
                    x: {
                        grid: {
                            drawOnChartArea: showGridLines,
                            color: showGridLines ? "rgba(220,220,220,0.4)" : "transparent",
                        },
                        ticks: { display: false, maxTicksLimit: 6 },
                        max: maxValue, // Set scale maximum to the highest data value
                    },
                    y: {
                        beginAtZero: true,
                        grid: { drawOnChartArea: false },
                        ticks: { font: { size: 14 }, callback: () => "" }, // hide labels
                    },
                },
                layout: { padding: { right: 20 } },
            },
        });
    }
    $effect(() => {
        buildChart();
        return () => chart?.destroy(); // cleanup before next run & on unmount
    });
</script>

<canvas
    bind:this={canvas}
    style="padding: 1em; width: 100%; max-height:300px; max-width: 900px;"
    width="1.5"
    height="1"
></canvas>

<style>
    @media (max-width: 600px) {
        canvas {
            max-height: 300px;
        }
    }
</style>
